import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  ReferenceLine,
  BarChart,
  Bar,
  Cell,
  LabelList,
  AreaChart,
  Area,
} from "recharts";
import { api, getErrMsg } from "../lib/api";
import { logError } from "../lib/logger";
import { formatTL, formatDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AiYorum } from "../components/AiYorum";
import { HaftalikOzet } from "../components/HaftalikOzet";
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  Percent,
  Coins,
  BadgeDollarSign,
  FileDown,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Lightbulb,
  ListTodo,
  ShieldAlert,
  Database,
  Trophy,
  Banknote,
  Activity,
  PieChart,
  Gauge,
  Plus,
  Minus,
  Trash2,
  ArrowDownCircle,
  ArrowUpCircle,
  Layers,
  Wrench,
  Briefcase,
  CalendarClock,
  Clock,
} from "lucide-react";

const AYLAR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const AYLAR_KISA = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const BOLUMLER5 = ["Kaporta", "Boya", "Mekanik", "Elektrik", "Trim", "Pasta-Cila", "Diğer", "Satış Sonrası Hizmetler"];

const kisaTL = (v) => {
  if (v == null) return "";
  const abs = Math.abs(v);
  if (abs >= 1e6) return `${(v / 1e6).toLocaleString("tr-TR", { maximumFractionDigits: 1 })} M`;
  if (abs >= 1e3) return `${Math.round(v / 1e3).toLocaleString("tr-TR")} b`;
  return Math.round(v).toLocaleString("tr-TR");
};

export default function DashboardPage() {
  const { user } = useAuth();
  const analizYetkili = user.role === "yonetici" || user.role === "goruntuleyici";
  const [data, setData] = useState(null);
  const [saglik, setSaglik] = useState(null);
  const [loading, setLoading] = useState(true);
  const suAnkiAy = new Date().getMonth() + 1;
  const [donem, setDonem] = useState(String(suAnkiAy)); // "1".."12" | "yil" | "aralik"
  const [aralik, setAralik] = useState({ bas: "", bit: "" });
  const [pdfLoading, setPdfLoading] = useState(false);
  const pdfRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { yil: 2026 };
      if (aralik.bas && aralik.bit) {
        params.aralik_bas = aralik.bas;
        params.aralik_bit = aralik.bit;
      }
      const res = await api.get("/dashboard", { params });
      setData(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Dashboard verileri yüklenemedi."));
    } finally {
      setLoading(false);
    }
  }, [aralik]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (aralik.bas && aralik.bit && data?.donemler?.aralik) setDonem("aralik");
  }, [data, aralik]);

  const aralikSec = (alan, deger) => {
    setAralik((a) => {
      const next = { ...a, [alan]: deger };
      if (next.bas && next.bit && parseInt(next.bas) > parseInt(next.bit)) {
        return { bas: next.bit, bit: next.bas };
      }
      return next;
    });
  };

  useEffect(() => {
    if (!analizYetkili) return;
    api
      .get("/finansal-analiz", { params: { yil: 2026 } })
      .then((res) => setSaglik(res.data))
      .catch(() => {});
  }, [analizYetkili]);

  const d = data?.donemler?.[donem];

  // Önceki aya göre değişim (Tüm Yıl ve Ocak'ta gösterilmez)
  const degisim = useCallback(
    (key) => {
      if (!data || donem === "yil" || donem === "1") return null;
      const prev = data.donemler[String(parseInt(donem) - 1)]?.[key];
      const curr = data.donemler[donem]?.[key];
      if (key === "brut_marj") {
        if (prev == null || curr == null) return null;
        return { tip: "puan", deger: curr - prev };
      }
      if (prev == null || prev === 0 || curr == null) return null;
      return { tip: "yuzde", deger: ((curr - prev) / Math.abs(prev)) * 100 };
    },
    [data, donem]
  );

  const donemAdi =
    donem === "yil" ? "Tüm Yıl"
    : donem === "aralik" ? `${AYLAR[parseInt(aralik.bas) - 1]} – ${AYLAR[parseInt(aralik.bit) - 1]}`
    : AYLAR[parseInt(donem) - 1];

  const ortalamaGelir = useMemo(() => {
    if (!data) return 0;
    const dolu = data.trend.filter((t) => t.gelir > 0);
    if (dolu.length === 0) return 0;
    return dolu.reduce((a, t) => a + t.gelir, 0) / dolu.length;
  }, [data]);

  const trendData = useMemo(
    () => (data ? data.trend.map((t) => ({ ...t, ad: AYLAR_KISA[t.ay - 1] })) : []),
    [data]
  );

  const bolumBarData = useMemo(() => {
    if (!d) return [];
    const rows = BOLUMLER5.map((b) => ({ bolum: b === "Satış Sonrası Hizmetler" ? "SSH" : b, tutar: d.bolum_ciro[b] || 0 }));
    if (d.bolumsuz_ciro > 0) rows.push({ bolum: "Belirtilmemiş", tutar: d.bolumsuz_ciro });
    return rows;
  }, [d]);

  // Son 12 ay KPI mini serileri (sparkline)
  const kpiSeriler = useMemo(() => {
    if (!data) return [];
    const aylik = (key) =>
      Array.from({ length: 12 }, (_, i) => ({
        ay: AYLAR_KISA[i],
        deger: data.donemler[String(i + 1)]?.[key] || 0,
      }));
    const doluAy = (seri) => seri.filter((s) => s.deger !== 0);
    const sonDegisim = (seri) => {
      const dolu = doluAy(seri);
      if (dolu.length < 2) return null;
      const onceki = dolu[dolu.length - 2].deger;
      const simdiki = dolu[dolu.length - 1].deger;
      if (onceki === 0) return null;
      return ((simdiki - onceki) / Math.abs(onceki)) * 100;
    };
    return [
      { key: "gelir", label: "Ciro", renk: "#2563EB", seri: aylik("gelir"), degisim: sonDegisim(aylik("gelir")) },
      { key: "gider_toplam", label: "Gider", renk: "#0D9488", seri: aylik("gider_toplam"), degisim: sonDegisim(aylik("gider_toplam")) },
      { key: "brut_kar", label: "Brüt Kâr", renk: "#16A34A", seri: aylik("brut_kar"), degisim: sonDegisim(aylik("brut_kar")) },
      { key: "net_kar", label: "Net Kâr", renk: "#7C3AED", seri: aylik("net_kar"), degisim: sonDegisim(aylik("net_kar")) },
    ];
  }, [data]);

  // Vurgu kartları: en yüksek gelir/gider/brüt kâr ayı + ortalama gelir
  const vurgular = useMemo(() => {
    if (!data) return null;
    const aylar = Array.from({ length: 12 }, (_, i) => ({
      ay: i + 1,
      ...data.donemler[String(i + 1)],
    }));
    const enYuksek = (key) => {
      const dolu = aylar.filter((a) => (a[key] || 0) > 0);
      if (dolu.length === 0) return null;
      return dolu.reduce((max, a) => (a[key] > max[key] ? a : max), dolu[0]);
    };
    const gelirli = aylar.filter((a) => a.gelir > 0);
    const ort = gelirli.length > 0 ? gelirli.reduce((s, a) => s + a.gelir, 0) / gelirli.length : 0;
    return {
      gelir: enYuksek("gelir"),
      gider: enYuksek("gider_toplam"),
      brutKar: enYuksek("brut_kar"),
      ortalamaGelir: ort,
      doluAySayisi: gelirli.length,
    };
  }, [data]);

  const handlePdf = async () => {
    if (!pdfRef.current) return;
    setPdfLoading(true);
    try {
      const canvas = await html2canvas(pdfRef.current, {
        scale: 2,
        backgroundColor: "#F4F5F7",
        useCORS: true,
        logging: false,
      });
      const img = canvas.toDataURL("image/jpeg", 0.82);
      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const w = 297;
      const h = 210;
      const imgH = (canvas.height * w) / canvas.width;
      let pos = 0;
      while (pos < imgH) {
        if (pos > 0) pdf.addPage();
        pdf.addImage(img, "JPEG", 0, -pos, w, imgH);
        pos += h;
      }
      pdf.save(`3A-Finans-Dashboard-${donemAdi.replace(" ", "-")}-2026.pdf`);
      toast.success("PDF raporu indirildi.");
    } catch (err) {
      logError(err);
      toast.error("PDF oluşturulamadı.");
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="space-y-4" data-testid="dashboard-loading">
        <Skeleton className="h-12 w-full" />
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
        <div className="grid lg:grid-cols-2 gap-4">
          <Skeleton className="h-72" />
          <Skeleton className="h-72" />
        </div>
      </div>
    );
  }

  if (!data || !d) return null;

  const kpiKartlar = [
    {
      key: "gelir",
      label: "Toplam Ciro",
      value: formatTL(d.gelir),
      renk: "#2563EB",
      icon: TrendingUp,
      chg: degisim("gelir"),
      alt: "Matrah (KDV hariç)",
      ekstra: { kdv: d.kdv, tahsil: d.tahsil_edilen, acik: d.bekleyen_tutar },
    },
    {
      key: "butce_toplam",
      label: "Toplam Gider",
      value: formatTL(d.toplam_gider_resmi ?? d.gider_resmi ?? d.butce_toplam),
      renk: "#0D9488",
      icon: Wallet,
      cift: {
        giderLabel: donem !== "yil" && donem !== "aralik" ? (d.ay_durumu === "kapali" ? "Gider (Gerçekleşen)" : "Gider (Planlanan)") : "Gider (Resmi)",
        gider: d.gider_resmi ?? d.butce_toplam,
        disKaynak: d.dis_kaynak ?? 0,
      },
      ayDurumu: donem !== "yil" && donem !== "aralik" ? d.ay_durumu : null,
    },
    { key: "brut_kar", label: "Brüt Kâr", value: formatTL(d.brut_kar), renk: "#16A34A", icon: Coins, chg: degisim("brut_kar"), alt: "Ciro (KDV hariç) − Toplam Gider (Dış Kaynak dahil)" },
    { key: "brut_marj", label: "Brüt Kâr Marjı", value: d.brut_marj != null ? `%${d.brut_marj.toLocaleString("tr-TR")}` : "—", renk: "#EA580C", icon: Percent, chg: degisim("brut_marj") },
    {
      key: "net_kar",
      label: "Net Kâr",
      value: formatTL(d.net_kar),
      renk: d.net_kar < 0 ? "#DC2626" : d.net_kar > 0 ? "#16A34A" : "#7C3AED",
      icon: BadgeDollarSign,
      chg: degisim("net_kar"),
      alt: "Ciro − SMM − Gider − Dış Kaynak",
      tint: d.net_kar < 0 ? "zarar" : d.net_kar > 0 ? "kar" : null,
    },
    {
      key: "yapilan_tahsilat",
      label: "Yapılan Tahsilat",
      value: formatTL(d.yapilan_tahsilat ?? 0),
      renk: "#0891B2",
      icon: Banknote,
      alt: `${d.yapilan_tahsilat_adet ?? 0} tahsilat işlemi`,
      yerler: d.tahsilat_yerler || [],
    },
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="space-y-4">
      {/* ÜST BAR */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 overflow-x-auto rounded-lg border bg-card p-1 max-w-full" data-testid="dashboard-donem-serit">
          <button
            onClick={() => { setDonem("yil"); setAralik({ bas: "", bit: "" }); }}
            data-testid="dashboard-donem-yil"
            className={`shrink-0 rounded-md px-3 py-2 text-xs font-medium transition-colors min-h-[36px] ${
              donem === "yil" ? "bg-[hsl(var(--accent))] text-[hsl(var(--primary))] font-semibold" : "text-muted-foreground hover:text-foreground hover:bg-muted"
            }`}
          >
            Tüm Yıl
          </button>
          {AYLAR_KISA.map((a, i) => (
            <button
              key={a}
              onClick={() => { setDonem(String(i + 1)); setAralik({ bas: "", bit: "" }); }}
              data-testid={`dashboard-donem-${i + 1}`}
              className={`shrink-0 rounded-md px-2.5 py-2 text-xs font-medium transition-colors min-h-[36px] ${
                donem === String(i + 1) ? "bg-[hsl(var(--accent))] text-[hsl(var(--primary))] font-semibold" : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              {a}
            </button>
          ))}
        </div>
        <div
          className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 ${donem === "aralik" ? "border-[hsl(var(--primary))]/40 bg-[hsl(var(--accent))]/60" : "bg-card"}`}
          data-testid="dashboard-aralik-secim"
        >
          <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground">Aralık:</span>
          <select
            value={aralik.bas}
            onChange={(e) => aralikSec("bas", e.target.value)}
            className="h-8 rounded-md border bg-card px-1.5 text-xs"
            data-testid="dashboard-aralik-bas"
          >
            <option value="">Ay seç</option>
            {AYLAR_KISA.map((a, i) => (
              <option key={a} value={i + 1}>{a}</option>
            ))}
          </select>
          <span className="text-xs text-muted-foreground">–</span>
          <select
            value={aralik.bit}
            onChange={(e) => aralikSec("bit", e.target.value)}
            className="h-8 rounded-md border bg-card px-1.5 text-xs"
            data-testid="dashboard-aralik-bit"
          >
            <option value="">Ay seç</option>
            {AYLAR_KISA.map((a, i) => (
              <option key={a} value={i + 1}>{a}</option>
            ))}
          </select>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-muted-foreground" data-testid="dashboard-son-veri">
            Son veri: <strong className="text-foreground">{data.son_veri_tarihi ? formatDate(data.son_veri_tarihi) : "—"}</strong>
          </span>
          <Button onClick={handlePdf} disabled={pdfLoading} variant="outline" size="sm" className="min-h-[40px] gap-1.5" data-testid="dashboard-pdf-button">
            {pdfLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
            PDF Raporu
          </Button>
        </div>
      </div>

      {/* VADE UYARI ŞERİDİ */}
      <VadeUyariSeridi />

      {/* PDF kapsamı */}
      <div ref={pdfRef} className="space-y-4 bg-background">
        <div className="hidden print-header" />
        {/* KPI KARTLARI */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="dashboard-kpi-kartlar">
          {kpiKartlar.map((k) => (
            <div
              key={k.key}
              className={`relative overflow-hidden rounded-xl border p-4 shadow-[0_1px_0_rgba(14,27,46,0.06),0_8px_24px_rgba(14,27,46,0.06)] ${
                k.tint === "zarar" ? "border-red-200 bg-red-50/80" : k.tint === "kar" ? "border-emerald-200 bg-emerald-50/70" : "bg-card"
              }`}
              data-testid={`dashboard-kpi-${k.key}`}
            >
              <div className="absolute inset-x-0 top-0 h-1" style={{ backgroundColor: k.renk }} />
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <k.icon className="h-3.5 w-3.5" style={{ color: k.renk }} />
                <span className="text-[10px] font-semibold uppercase tracking-wide">{k.label}</span>
                {k.ayDurumu && (
                  <span
                    className={`ml-auto rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                      k.ayDurumu === "kapali" ? "bg-slate-800 text-white" : "bg-emerald-100 text-emerald-700"
                    }`}
                    data-testid="dashboard-ay-durumu-rozet"
                  >
                    {k.ayDurumu === "kapali" ? "Kapandı" : "Açık"}
                  </span>
                )}
              </div>
              {k.cift ? (
                <div className="mt-2" data-testid="dashboard-gider-cift">
                  <p className="font-display text-lg xl:text-xl font-bold tabular-nums whitespace-nowrap" data-testid="dashboard-toplam-gider-buyuk">{k.value}</p>
                  <div className="mt-1 space-y-0.5 border-t border-border/60 pt-1">
                    <p className="flex items-center justify-between gap-2 text-[11px] tabular-nums text-muted-foreground">
                      <span>{k.cift.giderLabel}</span>
                      <span className="font-semibold text-foreground/80" data-testid="dashboard-gider-resmi">{formatTL(k.cift.gider)}</span>
                    </p>
                    <p className="flex items-center justify-between gap-2 text-[11px] tabular-nums text-muted-foreground">
                      <span>Dış Kaynak</span>
                      <span className="font-semibold text-foreground/80" data-testid="dashboard-gider-dis-kaynak">{formatTL(k.cift.disKaynak)}</span>
                    </p>
                  </div>
                </div>
              ) : (
                <p className={`mt-2 font-display text-lg xl:text-xl font-bold tabular-nums whitespace-nowrap ${k.tint === "zarar" ? "text-red-700" : k.tint === "kar" ? "text-emerald-700" : ""}`}>{k.value}</p>
              )}
              <div className="mt-1 flex items-center gap-1.5 min-h-[18px]">
                {k.chg ? (
                  <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold tabular-nums ${k.chg.deger >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid={`dashboard-kpi-${k.key}-degisim`}>
                    {k.chg.deger >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {k.chg.deger >= 0 ? "▲" : "▼"}{" "}
                    {k.chg.tip === "puan"
                      ? `${Math.abs(k.chg.deger).toLocaleString("tr-TR", { maximumFractionDigits: 1 })} puan`
                      : `%${Math.abs(k.chg.deger).toLocaleString("tr-TR", { maximumFractionDigits: 1 })}`}
                  </span>
                ) : (
                  <span className="text-[10px] text-muted-foreground">{k.alt || "\u00a0"}</span>
                )}
              </div>
              {k.ekstra && (
                <div className="mt-1.5 space-y-0.5 border-t border-border/60 pt-1.5" data-testid="dashboard-kpi-tahsilat-detay">
                  <p className="flex items-center justify-between text-[10px] tabular-nums">
                    <span className="text-muted-foreground">KDV</span>
                    <span className="font-semibold text-slate-600" data-testid="dashboard-kpi-kdv">{formatTL(k.ekstra.kdv)}</span>
                  </p>
                  <p className="flex items-center justify-between text-[10px] tabular-nums">
                    <span className="text-muted-foreground">Tahsil Edilen</span>
                    <span className="font-semibold text-emerald-700" data-testid="dashboard-kpi-tahsil-edilen">{formatTL(k.ekstra.tahsil)}</span>
                  </p>
                  <p className="flex items-center justify-between text-[10px] tabular-nums">
                    <span className="text-muted-foreground">Açıkta Bekleyen</span>
                    <span className="font-semibold text-amber-700" data-testid="dashboard-kpi-acik-bekleyen">{formatTL(k.ekstra.acik)}</span>
                  </p>
                  <p className="text-right text-[9px] text-muted-foreground">Tahsilatlar KDV dahildir</p>
                </div>
              )}
              {k.yerler && (
                <div className="mt-1.5 space-y-0.5 border-t border-border/60 pt-1.5" data-testid="dashboard-kpi-tahsilat-yerler">
                  {k.yerler.length === 0 ? (
                    <p className="text-[10px] text-muted-foreground">Bu dönemde tahsilat yok.</p>
                  ) : (
                    k.yerler.map((y) => (
                      <p key={y.odeme_yeri} className="flex items-center justify-between text-[10px] tabular-nums">
                        <span className="text-muted-foreground truncate">{y.odeme_yeri}</span>
                        <span className="font-semibold text-foreground/80" data-testid={`dashboard-tahsilat-yer-${y.odeme_yeri}`}>{formatTL(y.toplam)}</span>
                      </p>
                    ))
                  )}
                </div>
              )}
            </div>
          ))}
          {/* KASADAKİ TUTAR KARTI */}
          <KasaKarti />
        </div>

        {/* FİNANSAL SAĞLIK ŞERİDİ (yonetici + goruntuleyici) */}
        {analizYetkili && <SaglikSeridi saglik={saglik} />}

        {/* TAHSİLAT ÖZETİ KARTI */}
        <TahsilatOzetiKarti />

        {/* GRAFİKLER + KPI TREND */}
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-xl border bg-card p-4" data-testid="dashboard-trend-grafik">
            <h3 className="font-display text-sm font-semibold mb-1">Aylık Ciro & Gider Trendi (2026)</h3>
            <p className="text-[11px] text-muted-foreground mb-3">Ciro = matrah • Gider = resmi gider (ay durumuna göre) + Dış Kaynak + SMM • Kesikli çizgi: ortalama ciro</p>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 14% 90%)" />
                  <XAxis dataKey="ad" tick={{ fontSize: 11 }} stroke="hsl(220 9% 55%)" />
                  <YAxis tickFormatter={kisaTL} tick={{ fontSize: 10 }} stroke="hsl(220 9% 55%)" width={48} />
                  <RTooltip
                    formatter={(v, name) => [formatTL(v), name === "gelir" ? "Ciro" : "Gider"]}
                    labelFormatter={(l) => `${l} 2026`}
                    contentStyle={{ borderRadius: 8, fontSize: 12 }}
                  />
                  <ReferenceLine y={ortalamaGelir} stroke="#2563EB" strokeDasharray="6 4" strokeOpacity={0.5} />
                  <Line type="monotone" dataKey="gelir" stroke="#2563EB" strokeWidth={2.5} dot={{ r: 3 }} name="gelir" />
                  <Line type="monotone" dataKey="gider" stroke="#0D9488" strokeWidth={2.5} dot={{ r: 3 }} name="gider" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* KPI TREND GÖSTERGELERİ (sparkline) */}
          <div className="rounded-xl border bg-card p-4" data-testid="dashboard-kpi-trend">
            <h3 className="font-display text-sm font-semibold mb-1">KPI Trend Göstergeleri</h3>
            <p className="text-[11px] text-muted-foreground mb-3">Son 12 ay — aylık seyir</p>
            <div className="space-y-3">
              {kpiSeriler.map((s) => (
                <div key={s.key} className="flex items-center gap-3 rounded-lg border border-border/60 p-2.5" data-testid={`dashboard-sparkline-${s.key}`}>
                  <div className="w-20 shrink-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{s.label}</p>
                    <p className="text-sm font-bold tabular-nums whitespace-nowrap" style={{ color: s.renk }}>
                      {kisaTL(d[s.key])} ₺
                    </p>
                  </div>
                  <div className="h-10 flex-1 min-w-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={s.seri} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                        <Area type="monotone" dataKey="deger" stroke={s.renk} strokeWidth={1.8} fill={s.renk} fillOpacity={0.12} dot={false} />
                        <RTooltip
                          formatter={(v) => [formatTL(v), s.label]}
                          labelFormatter={(_, p) => `${p?.[0]?.payload?.ay} 2026`}
                          contentStyle={{ borderRadius: 8, fontSize: 11 }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="w-14 shrink-0 text-right">
                    {s.degisim != null ? (
                      <span
                        className={`inline-flex h-6 items-center gap-0.5 rounded-full px-1.5 text-[10px] font-bold tabular-nums ${
                          s.degisim >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
                        }`}
                      >
                        {s.degisim >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                        %{Math.abs(s.degisim).toLocaleString("tr-TR", { maximumFractionDigits: 0 })}
                      </span>
                    ) : (
                      <span className="text-[10px] text-muted-foreground">—</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* VURGU KARTLARI */}
        {vurgular && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="dashboard-vurgu-kartlar">
            <VurguKart
              icon={Trophy}
              renk="#2563EB"
              baslik="En Yüksek Ciro"
              altBaslik={vurgular.gelir ? `${AYLAR[vurgular.gelir.ay - 1]} 2026` : "—"}
              deger={vurgular.gelir ? formatTL(vurgular.gelir.gelir) : "—"}
              testid="dashboard-vurgu-gelir"
            />
            <VurguKart
              icon={Banknote}
              renk="#0D9488"
              baslik="En Yüksek Gider"
              altBaslik={vurgular.gider ? `${AYLAR[vurgular.gider.ay - 1]} 2026` : "—"}
              deger={vurgular.gider ? formatTL(vurgular.gider.gider_toplam) : "—"}
              testid="dashboard-vurgu-gider"
            />
            <VurguKart
              icon={Activity}
              renk="#16A34A"
              baslik="En Yüksek Brüt Kâr"
              altBaslik={vurgular.brutKar ? `${AYLAR[vurgular.brutKar.ay - 1]} 2026` : "—"}
              deger={vurgular.brutKar ? formatTL(vurgular.brutKar.brut_kar) : "—"}
              testid="dashboard-vurgu-brutkar"
            />
            <VurguKart
              icon={PieChart}
              renk="#E2B23C"
              baslik="Ortalama Ciro"
              altBaslik={`Son ${vurgular.doluAySayisi} ay ortalaması`}
              deger={formatTL(vurgular.ortalamaGelir)}
              testid="dashboard-vurgu-ortalama"
            />
          </div>
        )}

        {/* BÖLÜM GRAFİĞİ */}
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="rounded-xl border bg-card p-4" data-testid="dashboard-bolum-grafik">
            <h3 className="font-display text-sm font-semibold mb-1">Bölüm Cirosu — {donemAdi}</h3>
            <p className="text-[11px] text-muted-foreground mb-3">Seçili dönem, matrah (KDV hariç)</p>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bolumBarData} layout="vertical" margin={{ top: 5, right: 60, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 14% 90%)" horizontal={false} />
                  <XAxis type="number" tickFormatter={kisaTL} tick={{ fontSize: 10 }} stroke="hsl(220 9% 55%)" />
                  <YAxis type="category" dataKey="bolum" tick={{ fontSize: 11 }} width={90} stroke="hsl(220 9% 55%)" />
                  <RTooltip formatter={(v) => [formatTL(v), "Ciro"]} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="tutar" radius={[0, 6, 6, 0]} maxBarSize={26}>
                    {bolumBarData.map((row) => (
                      <Cell key={row.bolum} fill={row.bolum === "Belirtilmemiş" ? "#94A3B8" : "#E2B23C"} />
                    ))}
                    <LabelList dataKey="tutar" position="right" formatter={kisaTL} style={{ fontSize: 10, fill: "#0E1B2E" }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* GİDER YAPISI */}
          <div className="rounded-xl border bg-card p-4" data-testid="dashboard-gider-yapisi">
            <h3 className="font-display text-sm font-semibold mb-1">Ciro & Gider Yapısı — {donemAdi}</h3>
            <p className="text-[11px] text-muted-foreground mb-3">Ciro dağılımı: SMM, gider (bütçe planlanan) ve net sonuç</p>
            <GiderYapisi d={d} />
          </div>
        </div>

        {/* BÖLÜM PERFORMANS TABLOSU */}
        <BolumPerformans d={d} donemAdi={donemAdi} />

        {/* BÖLÜM & USTA PERFORMANSI (yan yana) */}
        <BolumUstaPaneli d={d} donem={donem} donemAdi={donemAdi} aralik={aralik} />

        {/* DIŞ KAYNAK (OUTSOURCE) TAKİP PANELİ */}
        <DisKaynakPaneli donem={donem} donemAdi={donemAdi} aralik={aralik} />

        {/* PERSONEL MALİYET KARŞILAŞTIRMASI (sadece Yönetici) */}
        {data.personel_karsilastirma && (
          <PersonelKarsilastirma pk={data.personel_karsilastirma} donem={donem} donemAdi={donemAdi} />
        )}

        {/* YÖNETİCİ ÖZETİ */}
        <YoneticiOzeti d={d} data={data} donem={donem} donemAdi={donemAdi} />

        {/* AI FİNANSAL ANALİZ */}
        <AiYorum tip="dashboard" yil={2026} donem={donem} baslik={`AI Finansal Analiz — ${donemAdi}`} />

        {/* HAFTALIK AI ÖZETİ */}
        <HaftalikOzet />

        {/* VERİ BESLEME DURUMU */}
        <VeriBesleme veri={data.veri_durumu} bugun={data.bugun} />
      </div>
    </motion.div>
  );
}

/* ---------- Vade Uyarı Şeridi (7 gün kala + geciken) ---------- */
function VadeUyariSeridi() {
  const [veri, setVeri] = useState(null);
  useEffect(() => {
    api
      .get("/ciro/vade-uyarilari")
      .then((res) => setVeri(res.data))
      .catch(() => {});
  }, []);
  if (!veri || (veri.geciken.adet === 0 && veri.yaklasan.adet === 0)) return null;
  return (
    <div
      className={`flex flex-wrap items-center gap-2.5 rounded-xl border bg-card px-4 py-3 ${veri.geciken.adet > 0 ? "border-red-200" : "border-amber-200"}`}
      data-testid="dashboard-vade-uyari-seridi"
    >
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
        <CalendarClock className="h-3.5 w-3.5" />
        VADE UYARILARI
      </span>
      {veri.geciken.adet > 0 && (
        <Link
          to="/ciro?tab=takip"
          className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-semibold tabular-nums text-red-700 transition-colors hover:bg-red-100"
          data-testid="dashboard-vade-geciken"
        >
          <AlertTriangle className="h-3 w-3" />
          Vadesi geçen: {veri.geciken.adet.toLocaleString("tr-TR")} kayıt • {formatTL(veri.geciken.tutar)}
        </Link>
      )}
      {veri.yaklasan.adet > 0 && (
        <Link
          to="/ciro?tab=takip"
          className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-semibold tabular-nums text-amber-800 transition-colors hover:bg-amber-100"
          data-testid="dashboard-vade-yaklasan"
        >
          <Clock className="h-3 w-3" />
          7 gün içinde vadesi dolacak: {veri.yaklasan.adet.toLocaleString("tr-TR")} kayıt • {formatTL(veri.yaklasan.tutar)}
        </Link>
      )}
      <Link
        to="/ciro?tab=takip"
        className="ml-auto text-[11px] font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        data-testid="dashboard-vade-takip-link"
      >
        Takip listesine git →
      </Link>
    </div>
  );
}

/* ---------- Kasadaki Tutar Kartı + Hareket Dialog'u ---------- */
const KAYNAK_ETIKET = { tahsilat: "Tahsilat", gider: "Gider", manuel: "Manuel" };
const KAYNAK_STIL = {
  tahsilat: "bg-blue-50 text-blue-700 border-blue-200",
  gider: "bg-orange-50 text-orange-700 border-orange-200",
  manuel: "bg-slate-100 text-slate-700 border-slate-200",
};

function KasaKarti() {
  const { user } = useAuth();
  const yetkili = user.role === "yonetici" || user.role === "operasyon";
  const [kasa, setKasa] = useState(null);
  const [open, setOpen] = useState(false);
  const [formTip, setFormTip] = useState(null); // "giris" | "cikis" | null
  const [tutar, setTutar] = useState("");
  const [tarih, setTarih] = useState(() => new Date().toISOString().slice(0, 10));
  const [aciklama, setAciklama] = useState("");
  const [kalemId, setKalemId] = useState("");
  const [kalemler, setKalemler] = useState([]);
  const [kaydediyor, setKaydediyor] = useState(false);

  const fetchKasa = useCallback(async () => {
    try {
      const res = await api.get("/kasa");
      setKasa(res.data);
    } catch (err) {
      logError("Kasa bilgisi yüklenemedi:", err);
    }
  }, []);

  useEffect(() => {
    fetchKasa();
  }, [fetchKasa]);

  // Çıkışta zorunlu gider kalemi seçimi için kalem listesi (personel/maaş kalemi hariç)
  useEffect(() => {
    if (!yetkili) return;
    api
      .get("/butce-kalemleri")
      .then((res) =>
        setKalemler(
          (res.data || []).filter((k) => k.kaynak !== "personel" && k.kalem_adi !== "Brüt Ücret ve Maaş Ödemeleri")
        )
      )
      .catch(() => {});
  }, [yetkili]);

  const dialogAc = (tip) => {
    setFormTip(tip);
    setOpen(true);
  };

  const kaydet = async () => {
    const t = parseFloat(String(tutar).replace(",", "."));
    if (!t || t <= 0) return toast.error("Geçerli bir tutar girin.");
    if (!tarih) return toast.error("Tarih seçin.");
    if (formTip === "cikis" && !kalemId) return toast.error("Kasa çıkışı için gider kalemi seçin.");
    setKaydediyor(true);
    try {
      await api.post("/kasa/hareket", {
        tip: formTip,
        tutar: t,
        tarih,
        aciklama,
        butce_kalemi_id: formTip === "cikis" ? kalemId : null,
      });
      toast.success(
        formTip === "giris" ? "Kasaya para eklendi." : "Kasadan para çıkarıldı ve gider defterine işlendi."
      );
      setTutar("");
      setAciklama("");
      setKalemId("");
      setFormTip(null);
      fetchKasa();
    } catch (err) {
      toast.error(getErrMsg(err, "Kasa hareketi kaydedilemedi."));
    } finally {
      setKaydediyor(false);
    }
  };

  const sil = async (id) => {
    try {
      await api.delete(`/kasa/hareket/${id}`);
      toast.success("Kasa hareketi silindi.");
      fetchKasa();
    } catch (err) {
      toast.error(getErrMsg(err, "Hareket silinemedi."));
    }
  };

  const bakiye = kasa?.bakiye ?? 0;
  const hareketler = kasa?.hareketler || [];

  return (
    <>
      <div
        onClick={() => {
          setFormTip(null);
          setOpen(true);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && setOpen(true)}
        className="relative overflow-hidden rounded-xl border bg-card p-4 text-left shadow-[0_1px_0_rgba(14,27,46,0.06),0_8px_24px_rgba(14,27,46,0.06)] cursor-pointer transition-transform duration-150 hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]"
        data-testid="dashboard-kpi-kasa"
      >
        <div className="absolute inset-x-0 top-0 h-1" style={{ backgroundColor: "#B8860B" }} />
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Banknote className="h-3.5 w-3.5" style={{ color: "#B8860B" }} />
          <span className="text-[10px] font-semibold uppercase tracking-wide">Kasadaki Tutar</span>
        </div>
        <p
          className={`mt-2 font-display text-lg xl:text-xl font-bold tabular-nums whitespace-nowrap ${bakiye < 0 ? "text-red-600" : ""}`}
          data-testid="dashboard-kasa-bakiye"
        >
          {formatTL(bakiye)}
        </p>
        {yetkili ? (
          <div className="mt-1.5 flex items-center gap-1.5">
            <button
              onClick={(e) => {
                e.stopPropagation();
                dialogAc("giris");
              }}
              className="inline-flex items-center gap-0.5 rounded-md border border-emerald-200 bg-emerald-50 px-1.5 py-1 text-[10px] font-semibold text-emerald-700 transition-colors hover:bg-emerald-100"
              data-testid="kasa-kart-ekle-btn"
            >
              <Plus className="h-3 w-3" /> Ekle
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                dialogAc("cikis");
              }}
              className="inline-flex items-center gap-0.5 rounded-md border border-red-200 bg-red-50 px-1.5 py-1 text-[10px] font-semibold text-red-700 transition-colors hover:bg-red-100"
              data-testid="kasa-kart-cikar-btn"
            >
              <Minus className="h-3 w-3" /> Çıkar
            </button>
          </div>
        ) : (
          <p className="mt-1 text-[10px] text-muted-foreground">Hareketler için tıklayın</p>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="kasa-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Kasa Hareketleri</DialogTitle>
            <DialogDescription>
              Tahsilatlarda "Kasa" seçilirse otomatik eklenir; giderlerde "Kasa" seçilirse otomatik düşülür.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-lg border bg-muted/40 p-2.5" data-testid="kasa-dialog-bakiye">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Bakiye</p>
              <p className={`font-display text-sm font-bold tabular-nums ${bakiye < 0 ? "text-red-600" : ""}`}>{formatTL(bakiye)}</p>
            </div>
            <div className="rounded-lg border bg-muted/40 p-2.5" data-testid="kasa-dialog-giris-toplam">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Toplam Giriş</p>
              <p className="font-display text-sm font-bold tabular-nums text-emerald-700">{formatTL(kasa?.giris_toplam ?? 0)}</p>
            </div>
            <div className="rounded-lg border bg-muted/40 p-2.5" data-testid="kasa-dialog-cikis-toplam">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Toplam Çıkış</p>
              <p className="font-display text-sm font-bold tabular-nums text-red-700">{formatTL(kasa?.cikis_toplam ?? 0)}</p>
            </div>
          </div>

          {yetkili && (
            <div className="rounded-lg border p-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setFormTip("giris")}
                  className={`flex-1 rounded-md border px-2 py-2 text-xs font-semibold transition-colors ${
                    formTip === "giris" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-border text-muted-foreground hover:bg-muted"
                  }`}
                  data-testid="kasa-form-tip-giris"
                >
                  <Plus className="mr-1 inline h-3.5 w-3.5" />
                  Para Ekle
                </button>
                <button
                  onClick={() => setFormTip("cikis")}
                  className={`flex-1 rounded-md border px-2 py-2 text-xs font-semibold transition-colors ${
                    formTip === "cikis" ? "border-red-300 bg-red-50 text-red-700" : "border-border text-muted-foreground hover:bg-muted"
                  }`}
                  data-testid="kasa-form-tip-cikis"
                >
                  <Minus className="mr-1 inline h-3.5 w-3.5" />
                  Para Çıkar
                </button>
              </div>
              {formTip && (
                <div className="mt-3 space-y-2.5">
                  {formTip === "cikis" && (
                    <div className="space-y-1">
                      <Label className="text-xs">Gider Kalemi *</Label>
                      <Select value={kalemId} onValueChange={setKalemId}>
                        <SelectTrigger className="h-10" data-testid="kasa-form-kalem-select">
                          <SelectValue placeholder="Kalem seçin (örn. Elektrik Tüketimi)" />
                        </SelectTrigger>
                        <SelectContent className="max-h-64">
                          {kalemler.map((k) => (
                            <SelectItem key={k.id} value={k.id} data-testid="kasa-form-kalem-option">
                              {k.kalem_adi}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-muted-foreground">
                        Çıkış, seçilen kaleme otomatik <strong>gerçekleşen gider</strong> olarak işlenir.
                      </p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2.5">
                    <div className="space-y-1">
                      <Label className="text-xs">Tutar ₺ *</Label>
                      <Input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        placeholder="0,00"
                        value={tutar}
                        onChange={(e) => setTutar(e.target.value)}
                        className="h-10 text-right tabular-nums"
                        data-testid="kasa-form-tutar-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Tarih *</Label>
                      <Input type="date" value={tarih} onChange={(e) => setTarih(e.target.value)} className="h-10" data-testid="kasa-form-tarih-input" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Açıklama</Label>
                    <Input
                      placeholder={formTip === "giris" ? "örn. Elden nakit girişi" : "örn. Nakit ödeme / düzeltme"}
                      value={aciklama}
                      onChange={(e) => setAciklama(e.target.value)}
                      className="h-10"
                      data-testid="kasa-form-aciklama-input"
                    />
                  </div>
                  <Button onClick={kaydet} disabled={kaydediyor} className="w-full min-h-[40px]" data-testid="kasa-form-kaydet-btn">
                    {kaydediyor && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                    {formTip === "giris" ? "Kasaya Ekle" : "Kasadan Çıkar"}
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="max-h-64 space-y-1.5 overflow-y-auto pr-1" data-testid="kasa-hareket-listesi">
            {hareketler.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground" data-testid="kasa-hareket-bos">
                Henüz kasa hareketi yok.
              </p>
            ) : (
              hareketler.map((h) => (
                <div key={h.id} className="flex items-center gap-2.5 rounded-lg border border-border/60 px-2.5 py-2" data-testid="kasa-hareket-satiri">
                  {h.tip === "giris" ? (
                    <ArrowUpCircle className="h-4 w-4 shrink-0 text-emerald-600" />
                  ) : (
                    <ArrowDownCircle className="h-4 w-4 shrink-0 text-red-600" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium">{h.aciklama || (h.tip === "giris" ? "Kasa girişi" : "Kasa çıkışı")}</p>
                    <p className="text-[10px] text-muted-foreground">{formatDate(h.tarih)}</p>
                  </div>
                  <Badge className={`shrink-0 border text-[9px] px-1.5 py-0 hover:bg-inherit ${KAYNAK_STIL[h.kaynak] || KAYNAK_STIL.manuel}`}>
                    {KAYNAK_ETIKET[h.kaynak] || h.kaynak}
                  </Badge>
                  <span
                    className={`shrink-0 text-xs font-bold tabular-nums whitespace-nowrap ${h.tip === "giris" ? "text-emerald-700" : "text-red-700"}`}
                    data-testid="kasa-hareket-tutar"
                  >
                    {h.tip === "giris" ? "+" : "−"}{formatTL(h.tutar)}
                  </span>
                  {yetkili && h.kaynak === "manuel" && (
                    <button
                      onClick={() => sil(h.id)}
                      className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600"
                      aria-label="Hareketi sil"
                      data-testid="kasa-hareket-sil-btn"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ---------- Tahsilat Özeti Kartı (tüm roller) ---------- */
function TahsilatOzetiKarti() {
  const [ozet, setOzet] = useState(null);
  const [hafta, setHafta] = useState(null);
  const [listeTipi, setListeTipi] = useState(null); // "vadesi_gelen" | "kesilen" | null
  useEffect(() => {
    api
      .get("/ciro/tahsilat-ozeti")
      .then((res) => setOzet(res.data))
      .catch(() => {});
    api
      .get("/ciro/hafta-ozet")
      .then((res) => setHafta(res.data))
      .catch(() => {});
  }, []);
  if (!ozet) return null;
  const enCokBekleyen = (ozet.sigorta_bekleyen || []).filter((s) => s.bekleyen > 0).slice(0, 3);
  const listeVeri = listeTipi && hafta ? hafta[listeTipi] : null;
  return (
    <div className="rounded-xl border bg-card p-4" data-testid="dashboard-tahsilat-ozeti">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-sm font-semibold">Tahsilat Özeti</h3>
        <div className="flex items-center gap-3">
          {hafta && (
            <span className="text-[10px] text-muted-foreground tabular-nums">
              Hafta: {formatDate(hafta.hafta_baslangic)} – {formatDate(hafta.hafta_bitis)}
            </span>
          )}
          <Link
            to="/ciro?tab=ozet"
            className="text-xs font-medium text-[hsl(var(--primary))] underline underline-offset-2"
            data-testid="dashboard-tahsilat-ozeti-link"
          >
            Detaya git →
          </Link>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <div data-testid="dashboard-tahsilat-tahsil">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Tahsil Edilen</p>
          <p className="mt-0.5 font-display text-base font-bold tabular-nums text-emerald-700">{formatTL(ozet.ozet.tahsil_edilen)}</p>
        </div>
        <div data-testid="dashboard-tahsilat-bekleyen">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Bekleyen</p>
          <p className="mt-0.5 font-display text-base font-bold tabular-nums text-amber-700">{formatTL(ozet.ozet.bekleyen)}</p>
        </div>
        <div data-testid="dashboard-tahsilat-kendine">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Yönetim Tahsilatı</p>
          <p className={`mt-0.5 font-display text-base font-bold tabular-nums ${ozet.ozet.yonetim_tahsilati_toplam > 0 ? "text-red-700" : ""}`}>
            {formatTL(ozet.ozet.yonetim_tahsilati_toplam)}
          </p>
        </div>
        <button
          type="button"
          onClick={() => hafta && setListeTipi("vadesi_gelen")}
          className="rounded-lg border border-sky-200 bg-sky-50/60 px-2.5 py-1.5 text-left transition-colors hover:bg-sky-100/60"
          data-testid="dashboard-tahsilat-hafta-vade"
        >
          <p className="text-[10px] font-semibold uppercase tracking-wide text-sky-700">Bu Hafta Vadesi Gelen</p>
          <p className="mt-0.5 font-display text-base font-bold tabular-nums text-sky-800">
            {hafta ? formatTL(hafta.vadesi_gelen.tutar) : "—"}
          </p>
          <p className="text-[10px] text-sky-700/80 tabular-nums">{hafta ? `${hafta.vadesi_gelen.adet} kayıt • listeyi aç` : ""}</p>
        </button>
        <button
          type="button"
          onClick={() => hafta && setListeTipi("kesilen")}
          className="rounded-lg border border-indigo-200 bg-indigo-50/60 px-2.5 py-1.5 text-left transition-colors hover:bg-indigo-100/60"
          data-testid="dashboard-tahsilat-hafta-kesilen"
        >
          <p className="text-[10px] font-semibold uppercase tracking-wide text-indigo-700">Bu Hafta Kesilen Faturalar</p>
          <p className="mt-0.5 font-display text-base font-bold tabular-nums text-indigo-800">
            {hafta ? formatTL(hafta.kesilen.tutar) : "—"}
          </p>
          <p className="text-[10px] text-indigo-700/80 tabular-nums">{hafta ? `${hafta.kesilen.adet} kayıt • listeyi aç` : ""}</p>
        </button>
      </div>
      {enCokBekleyen.length > 0 && (
        <div className="mt-3 border-t border-border/60 pt-2.5 space-y-1" data-testid="dashboard-tahsilat-bekleyen-liste">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">En Çok Bekleyen</p>
          {enCokBekleyen.map((s) => (
            <div key={s.ad} className="flex items-center justify-between gap-2 text-xs">
              <span className="truncate">{s.ad}</span>
              <span className="font-semibold tabular-nums text-amber-700 whitespace-nowrap">{formatTL(s.bekleyen)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Bu hafta liste dialogu */}
      <Dialog open={!!listeTipi} onOpenChange={(o) => !o && setListeTipi(null)}>
        <DialogContent className="bg-card max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">
              {listeTipi === "vadesi_gelen" ? "Bu Hafta Vadesi Gelen Ödemeler" : "Bu Hafta Kesilen Faturalar"}
            </DialogTitle>
            <DialogDescription>
              {hafta && `${formatDate(hafta.hafta_baslangic)} – ${formatDate(hafta.hafta_bitis)} • ${listeVeri ? listeVeri.adet : 0} kayıt • ${listeVeri ? formatTL(listeVeri.tutar) : ""}`}
            </DialogDescription>
          </DialogHeader>
          {listeVeri && listeVeri.kayitlar.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="dashboard-hafta-liste-tablo">
                <thead>
                  <tr className="border-b text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-2 py-2">{listeTipi === "vadesi_gelen" ? "Vade" : "Tarih"}</th>
                    <th className="px-2 py-2">Müşteri</th>
                    <th className="px-2 py-2">Sigorta</th>
                    <th className="px-2 py-2">Belge No</th>
                    <th className="px-2 py-2 text-right">{listeTipi === "vadesi_gelen" ? "Bekleyen ₺" : "Tutar ₺"}</th>
                  </tr>
                </thead>
                <tbody>
                  {listeVeri.kayitlar.map((k) => (
                    <tr key={k.id} className="border-b border-border/50">
                      <td className="px-2 py-2 whitespace-nowrap tabular-nums">
                        {formatDate(listeTipi === "vadesi_gelen" ? k.vade_tarihi : k.tarih)}
                      </td>
                      <td className="px-2 py-2 max-w-[180px] truncate" title={k.musteri_aciklama}>{k.musteri_aciklama || "—"}</td>
                      <td className="px-2 py-2 max-w-[130px] truncate">{k.sigorta_sirketi || "—"}</td>
                      <td className="px-2 py-2 whitespace-nowrap tabular-nums">{k.belge_no || "—"}</td>
                      <td className="px-2 py-2 text-right font-semibold tabular-nums whitespace-nowrap">
                        {formatTL(listeTipi === "vadesi_gelen" ? k.bekleyen : k.toplam, 2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="py-6 text-center text-sm text-muted-foreground">Bu hafta için kayıt bulunamadı.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Dış Kaynak (Outsource) Takip Paneli ---------- */
function DisKaynakPaneli({ donem, donemAdi, aralik }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const params = { yil: 2026 };
    if (donem === "aralik") {
      params.ay = parseInt(aralik.bas);
      params.ay_bit = parseInt(aralik.bit);
    } else if (donem !== "yil") {
      params.ay = parseInt(donem);
    }
    api
      .get("/dis-kaynak/ozet", { params })
      .then((res) => setData(res.data))
      .catch(() => setData({ hizmetler: [], toplam: 0, adet: 0 }));
  }, [donem, aralik]);

  const hizmetler = data?.hizmetler || [];
  const maxTutar = Math.max(...hizmetler.map((h) => h.tutar), 1);

  return (
    <div className="rounded-xl border bg-card p-4" data-testid="dashboard-dis-kaynak-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-sm font-semibold flex items-center gap-1.5">
          <Briefcase className="h-4 w-4 text-amber-600" />
          Dış Kaynak Giderleri (Outsource) <span className="text-xs font-normal text-muted-foreground">• {donemAdi}</span>
        </h3>
        {data && (
          <span className="text-xs font-semibold tabular-nums" data-testid="dashboard-dis-kaynak-toplam">
            {data.adet} işlem • {formatTL(data.toplam)}
          </span>
        )}
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">
        Bütçe kalemlerine karışmaz; her zaman gerçekleşen tutarıyla izlenir ve Toplam Gider'e ayrı satır olarak eklenir.
      </p>
      <div className="mt-3 space-y-2.5">
        {hizmetler.length === 0 ? (
          <p className="py-4 text-center text-xs text-muted-foreground" data-testid="dashboard-dis-kaynak-bos">
            Bu dönemde dış kaynak gideri yok. Fatura girişindeki "Dış Kaynak Hizmetleri" bölümünden veya Gider &gt; Dış Kaynak sekmesinden ekleyin.
          </p>
        ) : (
          hizmetler.slice(0, 8).map((h) => (
            <div key={h.hizmet_adi} className="space-y-1" data-testid="dashboard-dis-kaynak-satir">
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="truncate">
                  {h.hizmet_adi} <span className="text-muted-foreground">• {h.adet} işlem</span>
                </span>
                <span className="font-semibold tabular-nums whitespace-nowrap">{formatTL(h.tutar)}</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-amber-500" style={{ width: `${Math.max((h.tutar / maxTutar) * 100, 2)}%` }} />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* ---------- Bölüm & Usta Performansı (yan yana) ---------- */
function BolumUstaPaneli({ d, donem, donemAdi, aralik }) {
  const [ustaData, setUstaData] = useState(null);
  useEffect(() => {
    const params = { yil: 2026 };
    if (donem === "aralik") {
      params.ay = parseInt(aralik.bas);
      params.ay_bit = parseInt(aralik.bit);
    } else if (donem !== "yil") {
      params.ay = parseInt(donem);
    }
    api
      .get("/ciro/usta-rapor", { params })
      .then((res) => setUstaData(res.data))
      .catch(() => setUstaData({ ustalar: [] }));
  }, [donem, aralik]);

  const bolumler = Object.entries(d.bolum_ciro || {})
    .filter(([ad, v]) => v > 0 && ad !== "Belirtilmemiş")
    .sort((a, b) => b[1] - a[1]);
  const maxBolum = Math.max(...bolumler.map(([, v]) => v), 1);
  const ustalar = (ustaData?.ustalar || []).slice(0, 8);
  const maxUsta = Math.max(...ustalar.map((u) => u.toplam_matrah), 1);

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="dashboard-bolum-usta-panel">
      <div className="rounded-xl border bg-card p-4" data-testid="dashboard-bolum-performans">
        <h3 className="font-display text-sm font-semibold flex items-center gap-1.5">
          <Layers className="h-4 w-4 text-[hsl(var(--primary))]" />
          Bölüm Performansı <span className="text-xs font-normal text-muted-foreground">• {donemAdi}</span>
        </h3>
        <div className="mt-3 space-y-2.5">
          {bolumler.length === 0 ? (
            <p className="py-6 text-center text-xs text-muted-foreground">Bu dönemde bölüm cirosu yok.</p>
          ) : (
            bolumler.map(([ad, v]) => (
              <div key={ad} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="truncate">{ad === "Satış Sonrası Hizmetler" ? "SSH" : ad}</span>
                  <span className="font-semibold tabular-nums whitespace-nowrap">{formatTL(v)}</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-[hsl(var(--primary))]" style={{ width: `${Math.max((v / maxBolum) * 100, 2)}%` }} />
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      <div className="rounded-xl border bg-card p-4" data-testid="dashboard-usta-performans">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-display text-sm font-semibold flex items-center gap-1.5">
            <Wrench className="h-4 w-4 text-[hsl(var(--primary))]" />
            Usta Performansı <span className="text-xs font-normal text-muted-foreground">• {donemAdi}</span>
          </h3>
          <Link
            to="/ciro?tab=usta"
            className="text-xs font-medium text-[hsl(var(--primary))] underline underline-offset-2"
            data-testid="dashboard-usta-rapor-link"
          >
            Rapora git →
          </Link>
        </div>
        <div className="mt-3 space-y-2.5">
          {ustalar.length === 0 ? (
            <p className="py-6 text-center text-xs text-muted-foreground" data-testid="dashboard-usta-bos">
              Bu dönemde usta atanmış kayıt yok. Fatura / iş emri girerken usta seçin.
            </p>
          ) : (
            ustalar.map((u) => (
              <div key={u.id} className="space-y-1">
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="truncate">
                    {u.ad_soyad} <span className="text-muted-foreground">• {u.bolum || "—"} • {u.dosya_adedi} dosya</span>
                  </span>
                  <span className="font-semibold tabular-nums whitespace-nowrap">{formatTL(u.toplam_matrah)}</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-[hsl(var(--accent))]" style={{ width: `${Math.max((u.toplam_matrah / maxUsta) * 100, 2)}%` }} />
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Finansal Sağlık Şeridi (yonetici + goruntuleyici) ---------- */
const SAGLIK_STIL = {
  iyi: "bg-emerald-50 text-emerald-700 border-emerald-200",
  orta: "bg-amber-50 text-amber-800 border-amber-300",
  zayif: "bg-red-50 text-red-700 border-red-200",
};

function SaglikSeridi({ saglik }) {
  const oranFmt = (v, tip) => {
    if (v == null) return "—";
    if (tip === "gun") return `${v.toLocaleString("tr-TR", { maximumFractionDigits: 0 })} gün`;
    return v.toLocaleString("tr-TR", { maximumFractionDigits: 2 });
  };

  return (
    <div className="rounded-xl border bg-card px-4 py-3" data-testid="dashboard-saglik-seridi">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
          <Gauge className="h-3.5 w-3.5" />
          FİNANSAL SAĞLIK
        </span>
        {!saglik ? (
          <span className="text-xs text-muted-foreground">Yükleniyor…</span>
        ) : !saglik.bilanco_var ? (
          <span className="text-xs text-muted-foreground" data-testid="dashboard-saglik-bos">
            Bilanço verisi yok —{" "}
            <Link to="/finansal-analiz" className="font-medium text-[hsl(var(--primary))] underline underline-offset-2" data-testid="dashboard-saglik-bilanco-link">
              Finansal Analiz sayfasından girin
            </Link>
          </span>
        ) : (
          <>
            {[
              { key: "cari_oran", label: "Cari Oran", tip: "oran" },
              { key: "dso", label: "DSO", tip: "gun" },
              { key: "kaldirac", label: "Kaldıraç", tip: "oran" },
            ].map((k) => {
              const v = saglik.oranlar[k.key];
              const stil = v.durum ? SAGLIK_STIL[v.durum] : "bg-muted text-muted-foreground border-border";
              return (
                <span
                  key={k.key}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold tabular-nums ${stil}`}
                  data-testid={`dashboard-saglik-${k.key}`}
                >
                  {k.label}: {oranFmt(v.deger, k.tip)}
                </span>
              );
            })}
            <Link
              to="/finansal-analiz"
              className="ml-auto text-[11px] font-medium text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
              data-testid="dashboard-saglik-detay-link"
            >
              {`${AYLAR[saglik.kullanilan_ay - 1]} bilançosu • Detaylı analiz →`}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------- Vurgu Kartı ---------- */
const VurguKart = ({ icon: Icon, renk, baslik, altBaslik, deger, testid }) => (
  <div className="flex items-center gap-3 rounded-xl border bg-card p-4 shadow-[0_1px_0_rgba(14,27,46,0.06),0_8px_24px_rgba(14,27,46,0.06)]" data-testid={testid}>
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: `${renk}1A` }}>
      <Icon className="h-5 w-5" style={{ color: renk }} />
    </span>
    <div className="min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground truncate">{baslik}</p>
      <p className="font-display text-base font-bold tabular-nums whitespace-nowrap">{deger}</p>
      <p className="text-[11px] text-muted-foreground truncate">{altBaslik}</p>
    </div>
  </div>
);

/* ---------- Gelir & Gider Yapısı ---------- */
function GiderYapisi({ d }) {
  const gelir = d.gelir || 0;
  const pct = (v) => (gelir > 0 ? Math.min(Math.max((v / gelir) * 100, 0), 100) : 0);
  const satirlar = [
    { label: "SMM (Hizmet Maliyeti)", deger: d.smm, renk: "#EA580C" },
    { label: "Gider (Resmi Toplam)", deger: d.gider_resmi ?? d.butce_toplam, renk: "#0D9488" },
    { label: "Dış Kaynak (Outsource)", deger: d.dis_kaynak ?? 0, renk: "#F59E0B" },
    { label: "Net Kâr", deger: d.net_kar, renk: d.net_kar >= 0 ? "#16A34A" : "#DC2626" },
  ];
  return (
    <div className="flex h-64 flex-col justify-center space-y-4">
      <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2.5">
        <span className="text-xs font-semibold">Toplam Ciro</span>
        <span className="font-display text-sm font-bold tabular-nums">{formatTL(gelir)}</span>
      </div>
      {satirlar.map((s) => {
        const oran = pct(Math.abs(s.deger));
        return (
          <div key={s.label} className="space-y-1.5" data-testid={`dashboard-yapi-${s.label}`}>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{s.label}</span>
              <span className="tabular-nums font-semibold" style={{ color: s.renk }}>
                {formatTL(s.deger)}
                <span className="ml-1.5 text-[10px] text-muted-foreground font-normal">
                  {gelir > 0 ? `%${((s.deger / gelir) * 100).toLocaleString("tr-TR", { maximumFractionDigits: 1 })}` : "—"}
                </span>
              </span>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full transition-[width] duration-300" style={{ width: `${oran}%`, backgroundColor: s.renk }} />
            </div>
          </div>
        );
      })}
      <p className="text-[10px] text-muted-foreground">
        Oranlar seçili dönemin cirosuna göre hesaplanır. Net Kâr = Ciro − SMM − Resmi Gider (açık ay: Planlanan, kapalı ay: Gerçekleşen) − Dış Kaynak. Negatifse ciro giderleri karşılamıyor demektir.
      </p>
    </div>
  );
}

/* ---------- Personel Maliyet Karşılaştırması (Yönetici) ---------- */
function PersonelKarsilastirma({ pk, donem, donemAdi }) {
  const aralik = donem === "yil" ? [...Array(12).keys()] : [parseInt(donem) - 1];
  const ucret = aralik.reduce((a, i) => a + (pk.ucret_maliyet[i] || 0), 0);
  const butce = aralik.reduce((a, i) => a + (pk.butce_gerceklesen[i] || 0), 0);
  const fark = ucret - butce;
  const farkVar = Math.abs(fark) > 1;
  return (
    <div className="rounded-xl border bg-card p-4" data-testid="dashboard-personel-karsilastirma">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-display text-sm font-semibold">Personel Maliyeti Karşılaştırması — {donemAdi}</h3>
        {farkVar ? (
          <Badge className="bg-amber-50 text-amber-800 border border-amber-300 hover:bg-amber-50 gap-1" data-testid="personel-fark-uyari">
            <AlertTriangle className="h-3 w-3" />
            Fark: {formatTL(Math.abs(fark))} — kontrol edin
          </Badge>
        ) : (
          <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50 gap-1" data-testid="personel-fark-uyumlu">
            <CheckCircle2 className="h-3 w-3" />
            Uyumlu
          </Badge>
        )}
        <span className="ml-auto text-[10px] text-muted-foreground">KVKK — yalnızca Yönetici görür</span>
      </div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-lg border border-border/60 p-3" data-testid="personel-ucret-maliyet">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Personel Modülü — Toplam Maliyet</p>
          <p className="mt-1 font-display text-lg font-bold tabular-nums">{formatTL(ucret)}</p>
          <p className="text-[11px] text-muted-foreground">Ücret kayıtlarından (bordro brüt + nakit tamamlama)</p>
        </div>
        <div className={`rounded-lg border p-3 ${farkVar ? "border-amber-300 bg-amber-50/50" : "border-border/60"}`} data-testid="personel-butce-gerceklesen">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Bütçe Gerçekleşen — {pk.kategori}</p>
          <p className="mt-1 font-display text-lg font-bold tabular-nums">{formatTL(butce)}</p>
          <p className="text-[11px] text-muted-foreground">Gider & Bütçe modülü gerçekleşen tutarı</p>
        </div>
      </div>
    </div>
  );
}

/* ---------- Bölüm Performans Tablosu ---------- */
function BolumPerformans({ d, donemAdi }) {
  const rows = BOLUMLER5.map((b) => {
    const ciro = d.bolum_ciro[b] || 0;
    const smm = d.bolum_smm[b] || 0;
    const brut = ciro - smm;
    const marj = ciro > 0 ? (brut / ciro) * 100 : null;
    const pay = d.gelir > 0 ? (ciro / d.gelir) * 100 : 0;
    return { bolum: b, ciro, smm, brut, marj, pay };
  });
  const toplam = {
    ciro: rows.reduce((a, r) => a + r.ciro, 0) + (d.bolumsuz_ciro || 0),
    smm: rows.reduce((a, r) => a + r.smm, 0) + (d.bolum_smm["Belirtilmemiş"] || 0),
  };
  toplam.brut = toplam.ciro - toplam.smm;
  toplam.marj = toplam.ciro > 0 ? (toplam.brut / toplam.ciro) * 100 : null;

  const MiniBar = ({ pct, renk }) => (
    <div className="flex items-center gap-2 min-w-[90px]">
      <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${Math.min(Math.max(pct || 0, 0), 100)}%`, backgroundColor: renk }} />
      </div>
      <span className="text-[11px] tabular-nums text-muted-foreground w-10 text-right">
        {pct == null ? "—" : `%${pct.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}`}
      </span>
    </div>
  );

  return (
    <div className="rounded-xl border bg-card overflow-hidden" data-testid="dashboard-bolum-tablo">
      <div className="flex flex-wrap items-center gap-2 px-4 pt-4">
        <h3 className="font-display text-sm font-semibold">Bölüm Performansı — {donemAdi}</h3>
        {d.bolumsuz_ciro > 0 && (
          <Badge className="ml-auto bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-50 gap-1" data-testid="dashboard-bolumsuz-uyari">
            <AlertTriangle className="h-3 w-3" />
            {formatTL(d.bolumsuz_ciro)} bölüm etiketi bekliyor
          </Badge>
        )}
      </div>
      <div className="w-full overflow-x-auto mt-3">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-2.5">Bölüm</th>
              <th className="px-3 py-2.5 text-right">Ciro</th>
              <th className="px-3 py-2.5 text-right">SMM</th>
              <th className="px-3 py-2.5 text-right">Brüt Kâr</th>
              <th className="px-3 py-2.5">Marj</th>
              <th className="px-3 py-2.5">Ciro Payı</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.bolum} className="border-t border-border/60" data-testid="dashboard-bolum-satiri">
                <td className="px-4 py-2.5 font-medium whitespace-nowrap">{r.bolum}</td>
                <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{formatTL(r.ciro)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{formatTL(r.smm)}</td>
                <td className={`px-3 py-2.5 text-right tabular-nums whitespace-nowrap ${r.brut < 0 ? "text-red-700" : ""}`}>{formatTL(r.brut)}</td>
                <td className="px-3 py-2.5"><MiniBar pct={r.marj} renk="#16A34A" /></td>
                <td className="px-3 py-2.5"><MiniBar pct={r.pay} renk="#E2B23C" /></td>
              </tr>
            ))}
            <tr className="border-t-2 border-[hsl(var(--primary))]/20 bg-muted/40 font-semibold" data-testid="dashboard-bolum-toplam">
              <td className="px-4 py-2.5">TOPLAM</td>
              <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{formatTL(toplam.ciro)}</td>
              <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{formatTL(toplam.smm)}</td>
              <td className={`px-3 py-2.5 text-right tabular-nums whitespace-nowrap ${toplam.brut < 0 ? "text-red-700" : ""}`}>{formatTL(toplam.brut)}</td>
              <td className="px-3 py-2.5"><MiniBar pct={toplam.marj} renk="#16A34A" /></td>
              <td className="px-3 py-2.5"><MiniBar pct={100} renk="#E2B23C" /></td>
            </tr>
          </tbody>
        </table>
      </div>
      {/* SSH alt kırılımı */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 border-t px-4 py-3 text-xs text-muted-foreground" data-testid="dashboard-ssh-kirilim">
        <span className="font-semibold text-foreground">SSH Alt Kırılımı:</span>
        {["Pasta-Cila", "PPF", "Yıkama", "Diğer"].map((a) => (
          <span key={a} className="tabular-nums">
            {a}: <strong className="text-foreground">{formatTL(d.ssh_alt[a] || 0)}</strong>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ---------- Yönetici Özeti (kural tabanlı) ---------- */
function YoneticiOzeti({ d, data, donem, donemAdi }) {
  const guclu = [];
  if (d.net_kar > 0) guclu.push(`Dönem kârlı: ${formatTL(d.net_kar)} net kâr elde edildi.`);
  if (d.brut_marj != null && d.brut_marj > 30) guclu.push(`Brüt kâr marjı güçlü: %${d.brut_marj.toLocaleString("tr-TR")} (hedef üstü).`);
  if (d.islem_adedi > 0) guclu.push(`${d.islem_adedi.toLocaleString("tr-TR")} ciro işlemi kaydedildi.`);

  const riskler = [];
  if (d.butce_asim_kalem > 0) riskler.push(`${d.butce_asim_kalem} kalemde bütçe kullanımı %95'in üzerinde.`);
  if (d.net_kar < 0) riskler.push(`Dönem net sonucu negatif: ${formatTL(d.net_kar)}.`);
  if (data.alacak_60.tutar > 0) riskler.push(`60+ gün geciken alacak: ${formatTL(data.alacak_60.tutar)} (${data.alacak_60.adet} kayıt).`);

  const firsatlar = [];
  const enYuksek = BOLUMLER5.map((b) => ({ b, v: d.bolum_ciro[b] || 0 })).sort((a, x) => x.v - a.v)[0];
  if (enYuksek && enYuksek.v > 0) firsatlar.push(`En yüksek ciro: ${enYuksek.b} (${formatTL(enYuksek.v)}).`);
  const sshToplam = Object.values(d.ssh_alt).reduce((a, v) => a + v, 0);
  if (sshToplam > 0) {
    firsatlar.push(`SSH cirosu ${formatTL(sshToplam)} — Pasta-Cila, PPF ve yıkama ile büyüme alanı.`);
  } else {
    firsatlar.push("SSH (Pasta-Cila, PPF, Yıkama) satışları henüz düşük — çapraz satış fırsatı.");
  }

  const aksiyonlar = [];
  if (d.smm === 0 && d.gelir > 0) aksiyonlar.push(`${donemAdi} için SMM (maliyet) girişi yok — kârlılık hesabı için maliyet kayıtlarını girin.`);
  if (d.bolumsuz_ciro > 0) aksiyonlar.push(`${formatTL(d.bolumsuz_ciro)} ciro bölüm etiketi bekliyor — kayıtları düzenleyerek bölüm atayın.`);
  if (d.bekleyen_adet > 0) aksiyonlar.push(`${d.bekleyen_adet.toLocaleString("tr-TR")} kayıt tahsilat güncellemesi bekliyor (${formatTL(d.bekleyen_tutar)}).`);

  const kutular = [
    { baslik: "GÜÇLÜ NOKTALAR", renk: "border-emerald-200 bg-emerald-50/60", ikonRenk: "text-emerald-600", icon: CheckCircle2, maddeler: guclu, testid: "dashboard-ozet-guclu" },
    { baslik: "RİSKLER", renk: "border-red-200 bg-red-50/60", ikonRenk: "text-red-600", icon: ShieldAlert, maddeler: riskler, testid: "dashboard-ozet-riskler" },
    { baslik: "FIRSATLAR", renk: "border-blue-200 bg-blue-50/60", ikonRenk: "text-blue-600", icon: Lightbulb, maddeler: firsatlar, testid: "dashboard-ozet-firsatlar" },
    { baslik: "AKSİYONLAR", renk: "border-orange-200 bg-orange-50/60", ikonRenk: "text-orange-600", icon: ListTodo, maddeler: aksiyonlar, testid: "dashboard-ozet-aksiyonlar" },
  ];

  return (
    <div data-testid="dashboard-yonetici-ozeti">
      <h3 className="font-display text-sm font-semibold mb-2">Yönetici Özeti — {donemAdi}</h3>
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {kutular.map((k) => (
          <div key={k.baslik} className={`rounded-xl border p-3.5 ${k.renk}`} data-testid={k.testid}>
            <div className={`flex items-center gap-1.5 ${k.ikonRenk}`}>
              <k.icon className="h-4 w-4" />
              <span className="text-[11px] font-bold tracking-wide">{k.baslik}</span>
            </div>
            <ul className="mt-2 space-y-1.5">
              {k.maddeler.length === 0 ? (
                <li className="text-xs text-muted-foreground">Şu an için not yok.</li>
              ) : (
                k.maddeler.map((m, i) => (
                  <li key={`${i}-${String(m).slice(0, 40)}`} className="text-xs leading-relaxed text-foreground/85">• {m}</li>
                ))
              )}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Veri Besleme Durumu ---------- */
function VeriBesleme({ veri, bugun }) {
  const kaynaklar = [
    { key: "ciro", ad: "Ciro" },
    { key: "gider", ad: "Gider" },
    { key: "maliyet", ad: "Maliyet" },
  ];

  const guncelMi = (sonTarih) => {
    if (!sonTarih) return false;
    const fark = (new Date(bugun) - new Date(sonTarih)) / (1000 * 60 * 60 * 24);
    return fark <= 7;
  };

  return (
    <div className="rounded-xl border bg-card px-4 py-3" data-testid="dashboard-veri-besleme">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
          <Database className="h-3.5 w-3.5" />
          VERİ BESLEME
        </span>
        {kaynaklar.map((k) => {
          const v = veri[k.key];
          const guncel = guncelMi(v.son_tarih);
          return (
            <div key={k.key} className="flex items-center gap-2 text-xs" data-testid={`dashboard-veri-${k.key}`}>
              <span className="font-medium">{k.ad}:</span>
              <span className="tabular-nums text-muted-foreground">{v.adet.toLocaleString("tr-TR")} kayıt</span>
              <span className="text-muted-foreground">• Son: {v.son_tarih ? formatDate(v.son_tarih) : "—"}</span>
              {guncel ? (
                <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-50 text-[10px] px-1.5 py-0">Güncel</Badge>
              ) : (
                <Badge className="bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-50 text-[10px] px-1.5 py-0 gap-0.5">
                  <AlertTriangle className="h-2.5 w-2.5" />
                  {v.son_tarih ? "7+ gün eski" : "Veri yok"}
                </Badge>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
